import { gql } from '@apollo/client';

import { JobType } from 'shared/common/job-constants';
import { ClientData } from 'shared/models/client';
import { ContactType } from 'shared/models/contact';
import { JDUpdateContext } from 'shared/models/jd-llm-updates';
import {
    JobData,
    JobDiversity,
    JobIntroductionSettings,
    JobRecruiterPermissions,
    JobSubmissionSettings,
    JobTemplates,
    JobVisa
} from 'shared/models/job';
import { JobDescriptionData } from 'shared/models/job-description';

export interface JobFormData {
    __typename?: string;
    id: string;
    clientId: string;
    title: string;
    discipline: string;
    assignee: string;
    accountManagerId: string;
    status: number;
    jobType: JobType;
    placementFees: string;
    monthlyFees: number;
    accountManagerPercentage: number;
    recruiterPercentage: number;
    contactTypes: ContactType[];
    dailyEmailLimit: number;
    dailyPerUserEmailLimit: number;
    llmSequenceGenRatio: number;
    outreachAIContext: {
        directions: string;
        keyCriteria: string;
        highlights: string;
    };
    phoneScreenAIContext: {
        directions: string;
    };
    scoringAIContext: {
        scoringBrief: string;
        briefSource: 'agent' | 'recruiter';
        briefUpdatedAt: number;
    };
    submissionGenAIContext: {
        directions: string;
    };
    recruiterPermissions: JobRecruiterPermissions;
    submissionSettings: JobSubmissionSettings;
    introSettings: JobIntroductionSettings;
    templates: JobTemplates;
    description: {
        __typename?: string;
        _id: string;
        jobId: string;
        minExperienceYears: number;
        maxExperienceYears: number;
        diversity: JobDiversity;
        linkToJobDescription: string;
        visa: JobVisa[];
        compensation: string;
        description: string;
        interviewPrep: string;
        hiringManager: string;
        location: string;
        timezone: string;
    };
    client: {
        __typename?: string;
        id: string;
        name: string;
        description: string;
        foundedDate: string;
        founders: string;
        funding: string;
        hq: string;
        salesPitch: string;
        teamSize: string;
        website: string;
    };
}

export interface JobLastModificationAtData {
    modifiedAt: number;
    client: {
        modifiedAt: number;
    };
    description: {
        modifiedAt: number;
    };
}

export interface JobUpdateData {
    jobId: string;
    clientId: string;
    job: Omit<JobFormData, 'client' | 'id' | 'description'>;
    description: Omit<JobFormData['description'], '__typename' | 'jobId' | '_id'>;
    client: Omit<JobFormData['client'], '__typename' | 'id' | 'name'>;
}

export interface JDLLMUpdate {
    id: number;
    input: string;
    resultsFile: string;
    updatedAt: number;
    context: JDUpdateContext;
    inferenceId: string;
    episodeId: string;
    updates: {
        client?: { old: ClientData; new: ClientData };
        job?: { old: JobData; new: JobData };
        jobDescription?: { old: JobDescriptionData; new: JobDescriptionData };
    };
}

export interface JobOnePagerData {
    id: string;
    title: string;
    onePager: JobData['onePager'];
    onePagerData: JobData['onePagerData'];
    client: {
        id: string;
        logoUrl: string;
    };
}

export const CREATE_JOB = gql`
    mutation CreateJob($job: jobs_insert_input!) {
        job: insert_jobs_one(object: $job) {
            id
        }
    }
`;

export const UPDATE_JOB_WITH_CLIENT_AND_DESCRIPTION = gql`
    mutation UpdateJob(
        $jobId: String!
        $clientId: String!
        $job: jobs_set_input!
        $description: job_descriptions_set_input!
        $client: clients_set_input!
    ) {
        update_clients_by_pk(pk_columns: { id: $clientId }, _set: $client) {
            id
        }
        update_jobs_by_pk(pk_columns: { id: $jobId }, _set: $job) {
            id
        }
        update_job_descriptions_by_pk(pk_columns: { jobId: $jobId }, _set: $description) {
            id: jobId
            jobId
        }
    }
`;

export const UPDATE_JOB_DESCRIPTION = gql`
    mutation UpdateJobDescription($jobId: String!, $description: job_descriptions_set_input!) {
        update_job_descriptions_by_pk(pk_columns: { jobId: $jobId }, _set: $description) {
            id: jobId
            jobId
        }
    }
`;

export const JOB_FORM_DATA = gql`
    query JobFormData($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            clientId
            title
            discipline
            assignee
            accountManagerId
            status
            jobType
            placementFees
            monthlyFees
            accountManagerPercentage
            recruiterPercentage
            contactTypes
            dailyEmailLimit
            dailyPerUserEmailLimit
            outreachAIContext
            phoneScreenAIContext
            scoringAIContext
            submissionGenAIContext
            recruiterPermissions
            submissionSettings
            introSettings
            templates
            llmSequenceGenRatio
            description {
                _id: jobId
                jobId
                minExperienceYears
                maxExperienceYears
                diversity
                linkToJobDescription
                visa
                compensation
                description
                interviewPrep
                hiringManager
                location
                timezone
            }
            client {
                id
                name
                description
                founders
                funding
                hq
                salesPitch
                teamSize
                foundedDate
                website
            }
        }
    }
`;

export const JOB_ONE_PAGER = gql`
    query JobOnePager($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            title
            onePager
            onePagerData
            client {
                id
                logoUrl
            }
        }
    }
`;

export const JOB_LAST_MODIFICATION_AT = gql`
    subscription JobLastModificationAt($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            modifiedAt
            client {
                id
                modifiedAt
            }
            description {
                jobId
                modifiedAt
            }
        }
    }
`;

export const JOB_UNDO_LLM_UPDATE = gql`
    mutation JobUndoLLMUpdate($updateId: Int!) {
        undo_jd_llm_update(args: { update_id: $updateId }) {
            result
        }
    }
`;

export const JOB_LLM_UPDATES_HISTORY = gql`
    query JobLLMUpdatesHistory($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            clientId
            title
            client {
                id
                name
            }
            assignee
            accountManagerId
        }
        updates: history_jd_llm_updates(
            where: { job: { id: { _eq: $jobId } }, updates: { _is_null: false } }
            order_by: { updatedAt: desc }
        ) {
            id
            input
            resultsFile
            inferenceId
            episodeId
            updatedAt
            context
            updates
        }
    }
`;

export const UPDATE_JOB_ONE_PAGER_DATA = gql`
    mutation UpdateJobOnePagerData($jobId: String!, $onePagerData: jsonb) {
        update_jobs_by_pk(pk_columns: { id: $jobId }, _set: { onePagerData: $onePagerData }) {
            id
        }
    }
`;
